/**
 * Pure rules for the document-wording editor, kept out of the component so
 * they can be tested without a browser.
 */
import type { PlatformSetting } from '../../services/adminSettings.service';

/** Mirrors the API's limit on a value. */
export const MAX_VALUE_LENGTH = 4000;

function humanise(raw: string): string {
  const spaced = raw.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `documents.subscription.intro` -> `Subscription` (the document it belongs to). */
export function documentOf(key: string): string {
  const parts = key.split('.');
  return humanise(parts.length > 2 ? parts[1] : parts[0]);
}

/** `documents.subscription.intro_text` -> `Intro text`. */
export function labelOf(key: string): string {
  const parts = key.split('.');
  return humanise(parts.slice(2).join(' ') || parts[parts.length - 1]);
}

/** Settings grouped by document, in the order the API returned them. */
export function groupByDocument(
  items: readonly PlatformSetting[],
): { document: string; items: PlatformSetting[] }[] {
  const groups = new Map<string, PlatformSetting[]>();
  for (const item of items) {
    const name = documentOf(item.key);
    groups.set(name, [...(groups.get(name) ?? []), item]);
  }
  return [...groups].map(([document, rows]) => ({ document, items: rows }));
}

export function isUnchanged(stored: string, draft: string): boolean {
  return stored === draft;
}

/** Why Save is off, in words for the screen — null when it can be pressed. */
export function saveBlockedReason(stored: string, draft: string, busy: boolean): string | null {
  if (busy) return 'Saving…';
  if (isUnchanged(stored, draft)) return 'Nothing has changed yet.';
  if (draft.length > MAX_VALUE_LENGTH) {
    return `This text is ${draft.length - MAX_VALUE_LENGTH} characters over the ${MAX_VALUE_LENGTH} limit.`;
  }
  return null;
}

/** What the field accepts, in plain words, for the hint under it. */
export function valueTypeHint(valueType: string): string {
  switch (valueType) {
    case 'bool':
      return 'Takes true or false.';
    case 'int':
      return 'Takes a whole number.';
    case 'json':
      return 'Takes valid JSON.';
    default:
      return 'Free text.';
  }
}
